import { executeSearchRequest } from 'tio-data/requests/search';
import type { Store } from 'tio-data';
import {
  OpenSearchQueryBuilder,
  classifySearchToken,
  type FilterClause,
} from './open-search-builder.ts';
import { OpenSearchResponseBuilder } from './open-search-response-builder.ts';
import type { OpenSearchResponse, Person } from 'tio-data/data/resources/search.ext';

const DEFAULT_LIMIT = 10;
const EMPLOYEE_NAME_FIELDS = [
  'employees_nested.first_name_text',
  'employees_nested.last_name_text',
];

type PersonSearchEmployee = Person['employees'][number] & {
  company_id?: string | number;
  first_name?: string;
  last_name?: string;
  payroll_id?: string;
};

export type EmployeeSearchResult = {
  id: string;
  fullName: string;
  payrollId?: string;
  email?: string;
};

/**
 * Builds the company-scoped Person-index search used by employee typeaheads.
 * The outer company_ids predicate satisfies SearchPolicy. The actual company
 * and identity predicates must share one employees_nested object, otherwise a
 * person employed by two companies can match a name in one employment and be
 * returned for the other.
 */
export function buildCompanyEmployeeSearchBody(
  search: string,
  companyId: string,
  limit = DEFAULT_LIMIT
) {
  const builder = new OpenSearchQueryBuilder().setPagination(1, limit);
  builder.mustTerm('company_ids', companyId);

  const tokens = search.trim().split(/\s+/).filter(Boolean);
  const nameTokens: string[] = [];
  const identityClauses: FilterClause[] = [];

  for (const token of tokens) {
    const kind = classifySearchToken(token);
    if (kind === 'email') {
      identityClauses.push({
        match: {
          'employees_nested.email_text': { query: token, operator: 'and', _name: 'email' },
        },
      });
    } else if (kind === 'identifier') {
      identityClauses.push({
        term: { 'employees_nested.payroll_id_keyword': { value: token, _name: 'payroll' } },
      });
      identityClauses.push({
        term: { 'employees_nested.employee_id_keyword': { value: token, _name: 'employee_id' } },
      });
    } else {
      nameTokens.push(token);
    }
  }

  if (nameTokens.length) {
    const phrase = nameTokens.join(' ');
    identityClauses.push({
      multi_match: {
        query: phrase,
        fields: EMPLOYEE_NAME_FIELDS,
        type: 'best_fields',
        operator: 'or',
        fuzziness: 'AUTO',
        lenient: true,
        _name: 'name',
      },
    });
    identityClauses.push({
      multi_match: {
        query: phrase,
        fields: EMPLOYEE_NAME_FIELDS,
        type: 'cross_fields',
        operator: 'and',
        lenient: true,
        _name: 'name_exact',
      },
    });
  }

  builder.addFilter({
    nested: {
      path: 'employees_nested',
      query: {
        bool: {
          filter: [{ term: { 'employees_nested.company_id_keyword': companyId } }],
          should: identityClauses,
          minimum_should_match: 1,
        },
      },
    },
  });

  return builder.selectSourceFields(['employees']).build();
}

/** Extracts the selected company’s employee records from Person search hits. */
export function employeeSearchResultsFromPeople(people: Person[], companyId: string) {
  const results = new Map<string, EmployeeSearchResult>();

  people.forEach((person) => {
    person.employees?.forEach((employee) => {
      const employeeResult = employee as PersonSearchEmployee;
      const employeeCompanyId = employeeResult.companyId ?? employeeResult.company_id;
      if (String(employeeCompanyId) !== String(companyId)) return;

      const id = String(employeeResult.id);
      const firstName = employeeResult.firstName ?? employeeResult.first_name;
      const lastName = employeeResult.lastName ?? employeeResult.last_name;
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim() || person.fullName;

      results.set(id, {
        id,
        fullName,
        payrollId: employeeResult.payrollId ?? employeeResult.payroll_id,
        email: employeeResult.email,
      });
    });
  });

  return [...results.values()];
}

export async function searchCompanyEmployees(
  store: Store,
  search: string,
  companyId: string,
  limit = DEFAULT_LIMIT
) {
  const term = search.trim();
  if (!term || !companyId) return [];

  const { content } = (await store.request(
    executeSearchRequest({
      'model-type': 'Person',
      'search-body': buildCompanyEmployeeSearchBody(term, companyId, limit),
    })
  )) as { content: OpenSearchResponse<Person> };

  const people = OpenSearchResponseBuilder.from<Person>(content).getDocuments();
  return employeeSearchResultsFromPeople(people, companyId);
}
